import { useEffect, useState } from "react";
import { CheckCircle2, Eye, EyeOff } from "lucide-react";
import { toast } from "sonner";

import { authClient } from "@/lib/auth-client";
import { cn } from "@/lib/utils";
import { useBetaInformation } from "@/hooks/queries/useBetaInformation";
import {
  useWhatsappStatus,
  isWhatsappActive,
} from "@/hooks/queries/useWhatsappStatus";
import { useOptOutWhatsapp } from "@/hooks/mutations/useWhatsappMutations";
import { WhatsappOptinDialog } from "@/components/settings/WhatsappOptinDialog";
import { WhatsappIcon, WHATSAPP_BTN } from "@/components/common/whatsapp-brand";
import { Separator } from "@/components/ui/separator";

const CARD =
  "rounded-2xl border border-slate-100 bg-white p-6 shadow-sm dark:border-slate-800 dark:bg-slate-900";
const INPUT =
  "w-full rounded-xl border border-slate-200 bg-slate-50 px-4 py-2.5 text-sm text-slate-800 outline-none transition placeholder:text-slate-400 focus:border-primary focus:ring-2 focus:ring-primary/30 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-100";
const BTN =
  "shrink-0 rounded-xl bg-primary px-5 py-2.5 text-sm font-bold text-primary-foreground transition hover:bg-primary/90 disabled:cursor-not-allowed disabled:opacity-50";

function SectionHeader({
  title,
  subtitle,
}: {
  title: string;
  subtitle: string;
}) {
  return (
    <div className="mb-5">
      <h2 className="text-base font-extrabold text-slate-800 dark:text-slate-100">
        {title}
      </h2>
      <p className="mt-0.5 text-sm text-slate-400 dark:text-slate-500">
        {subtitle}
      </p>
    </div>
  );
}

function PasswordInput({
  value,
  onChange,
  placeholder,
}: {
  value: string;
  onChange: (v: string) => void;
  placeholder: string;
}) {
  const [show, setShow] = useState(false);
  return (
    <div className="relative">
      <input
        type={show ? "text" : "password"}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        className={cn(INPUT, "pr-11")}
      />
      <button
        type="button"
        onClick={() => setShow((s) => !s)}
        className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 dark:hover:text-slate-300"
      >
        {show ? (
          <EyeOff className="size-[18px]" />
        ) : (
          <Eye className="size-[18px]" />
        )}
      </button>
    </div>
  );
}

/**
 * Activación de las notificaciones por WhatsApp. La verificación ocurre fuera de
 * la app: el usuario nos escribe con un código y el número desde el que escribe
 * queda verificado, así que aquí solo se revisa/corrige el celular y se abre el
 * chat. El estado se refresca solo mientras el diálogo de activación está abierto.
 */
function WhatsappNotifications() {
  const { data: beta } = useBetaInformation();
  const optOut = useOptOutWhatsapp();
  const [optinOpen, setOptinOpen] = useState(false);
  // Se sondea solo con el diálogo abierto: es el único momento en que el usuario
  // está esperando que su mensaje de WhatsApp llegue.
  const { data: status } = useWhatsappStatus({ poll: optinOpen });

  const registeredNumber = beta?.userextrainformation_contactnumber ?? "";
  const isActive = isWhatsappActive(status);

  async function deactivate() {
    try {
      await optOut.mutateAsync();
      toast.success("Notificaciones por WhatsApp desactivadas");
    } catch {
      toast.error("No se pudo desactivar");
    }
  }

  return (
    <div className={CARD}>
      <SectionHeader
        title="Notificaciones por WhatsApp"
        subtitle="Recibe tus novedades también por WhatsApp"
      />

      {isActive ? (
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <div className="flex items-center gap-2.5">
            <CheckCircle2 className="size-5 text-emerald-500" />
            <div>
              <p className="text-sm font-bold text-slate-800 dark:text-slate-100">
                Activo en +{status?.number}
              </p>
              <p className="text-xs text-slate-400 dark:text-slate-500">
                También puedes responder BAJA por WhatsApp para desactivarlo.
              </p>
            </div>
          </div>
          <button
            onClick={deactivate}
            disabled={optOut.isPending}
            className="shrink-0 rounded-xl border border-slate-200 px-5 py-2.5 text-sm font-bold text-slate-600 transition hover:bg-slate-50 disabled:opacity-50 dark:border-slate-700 dark:text-slate-300 dark:hover:bg-slate-800"
          >
            {optOut.isPending ? "Desactivando..." : "Desactivar"}
          </button>
        </div>
      ) : (
        <div className="flex flex-col gap-3">
          {/* Solo lectura: el número que cuenta es el que nos escriba, no uno tipeado.
              Sin celular registrado no se muestra: un campo vacío solo confunde. */}
          {registeredNumber ? (
            <input
              type="tel"
              value={registeredNumber}
              disabled
              className="w-full cursor-not-allowed rounded-xl border border-slate-200 bg-slate-100 px-4 py-2.5 text-sm text-slate-500 dark:border-slate-700 dark:bg-slate-800/60 dark:text-slate-400"
            />
          ) : null}

          {/* <p className="text-xs text-slate-400 dark:text-slate-500">
            Al activar te abriremos un chat con un mensaje listo: solo tienes
            que enviarlo. Quedará verificado el celular desde el que nos
            escribas, así que puedes hacerlo desde otro sin problema.
          </p> */}

          {status?.optinUrl && (
            <button onClick={() => setOptinOpen(true)} className={WHATSAPP_BTN}>
              <WhatsappIcon />
              Activar por WhatsApp
            </button>
          )}
        </div>
      )}

      {/* Fuera del ternario: si se desmontara al verificarse, el modal nunca
          alcanzaría a mostrar la confirmación. */}
      {status?.optinUrl && (
        <WhatsappOptinDialog
          open={optinOpen}
          onOpenChange={setOptinOpen}
          optinUrl={status.optinUrl}
          verified={isActive}
        />
      )}
    </div>
  );
}

export default function AccountSettings() {
  const { data: session } = authClient.useSession();
  const [name, setName] = useState("");
  const [savingName, setSavingName] = useState(false);

  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [savingPassword, setSavingPassword] = useState(false);

  useEffect(() => {
    setName(session?.user?.name ?? "");
  }, [session?.user?.name]);

  async function saveName() {
    if (!name.trim()) return;
    setSavingName(true);
    const { error } = await authClient.updateUser({ name: name.trim() });
    setSavingName(false);
    if (error) {
      toast.error(error.message || "No se pudo actualizar el nombre");
      return;
    }
    toast.success("Nombre actualizado correctamente");
  }

  async function changePassword() {
    if (!currentPassword || !newPassword || !confirmPassword) {
      toast.error("Completa todos los campos");
      return;
    }
    if (newPassword !== confirmPassword) {
      toast.error("Las contraseñas nuevas no coinciden");
      return;
    }
    if (newPassword.length < 8) {
      toast.error("La contraseña debe tener al menos 8 caracteres");
      return;
    }
    setSavingPassword(true);
    const { error } = await authClient.changePassword({
      currentPassword,
      newPassword,
      revokeOtherSessions: false,
    });
    setSavingPassword(false);
    if (error) {
      toast.error(error.message || "No se pudo cambiar la contraseña");
      return;
    }
    setCurrentPassword("");
    setNewPassword("");
    setConfirmPassword("");
    toast.success("Contraseña actualizada correctamente");
  }

  return (
    <div className="flex w-full flex-col gap-5">
      {/* Nombre */}
      <div className={CARD}>
        <SectionHeader
          title="Nombre"
          subtitle="Este nombre se muestra en tu perfil"
        />
        <div className="flex flex-col gap-3 sm:flex-row">
          <input
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="Tu nombre"
            className={cn(INPUT, "flex-1")}
          />
          <button
            onClick={saveName}
            disabled={savingName || !name.trim()}
            className={BTN}
          >
            {savingName ? "Guardando..." : "Guardar"}
          </button>
        </div>
      </div>

      {/* Correo electrónico (read-only) */}
      <div className={CARD}>
        <SectionHeader
          title="Correo electrónico"
          subtitle="Para cambiar tu correo contacta al soporte"
        />
        <input
          type="email"
          value={session?.user?.email ?? ""}
          disabled
          className="w-full cursor-not-allowed rounded-xl border border-slate-200 bg-slate-100 px-4 py-2.5 text-sm text-slate-500 dark:border-slate-700 dark:bg-slate-800/60 dark:text-slate-400"
        />
      </div>

      <WhatsappNotifications />

      {/* Separa las preferencias de la zona sensible: cambiar la contraseña no es
          un ajuste más. */}
      <Separator className="my-2" />

      {/* Contraseña */}
      <div className={CARD}>
        <SectionHeader title="Contraseña" subtitle="Mínimo 8 caracteres" />
        <div className="flex flex-col gap-3">
          <PasswordInput
            value={currentPassword}
            onChange={setCurrentPassword}
            placeholder="Contraseña actual"
          />
          <PasswordInput
            value={newPassword}
            onChange={setNewPassword}
            placeholder="Nueva contraseña"
          />
          <PasswordInput
            value={confirmPassword}
            onChange={setConfirmPassword}
            placeholder="Confirmar nueva contraseña"
          />
          <button
            onClick={changePassword}
            disabled={
              savingPassword ||
              !currentPassword ||
              !newPassword ||
              !confirmPassword
            }
            className={cn(BTN, "w-full")}
          >
            {savingPassword ? "Actualizando..." : "Cambiar contraseña"}
          </button>
        </div>
      </div>
    </div>
  );
}
